import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { LayoutGroup, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { isValidElement, type ReactNode, useCallback } from "react";

interface CarouselProps {
	children: ReactNode | ReactNode[];
	title: ReactNode | string;
	href?: string;
	hasMore?: boolean;
	className?: string;
}

export default function Carousel({
	children,
	title,
	href,
	className,
}: CarouselProps) {
	const nodes = Array.isArray(children) ? children : [children];
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{
			loop: false,
			slidesToScroll: 1,
			align: `start`,
		},
		[WheelGesturesPlugin() as any],
	);

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	return (
		<div className="flex w-full flex-col gap-4 phablet:gap-4">
			<div className="flex items-center justify-between sm:justify-between">
				{isValidElement(title) ? (
					title
				) : !href ? (
					<h2 className="group flex items-center gap-1 text-center font-medium phablet:font-semibold text-xl sm:text-left sm:text-2xl">
						{title}:
					</h2>
				) : (
					<Link href={href} className="flex">
						<h2 className="group items-center text-left font-medium phablet:font-semibold text-xl sm:text-xl">
							{title}
							<ChevronRight className="ml-1 inline-block h-5 phablet:h-auto phablet:w-auto w-5 shrink-0 text-slate-500 transition-all group-hover:translate-x-1 group-hover:scale-110 group-hover:text-slate-600 dark:text-slate-300 dark:group-hover:text-slate-400" />
						</h2>
					</Link>
				)}

				<div className="hidden text-xl sm:flex dark:text-gray-50">
					<button
						className="rounded-full p-2 hover:bg-black/5 hover:dark:bg-white/5"
						onClick={scrollPrev}
					>
						<ChevronLeft />
					</button>
					<button
						className="rounded-full p-2 hover:bg-black/5 hover:dark:bg-white/5"
						onClick={scrollNext}
					>
						<ChevronRight />
					</button>
				</div>
			</div>

			<div className="relative w-full" ref={nodes ? emblaRef : undefined}>
				<div className="ml-4 tablet:ml-0 flex w-full gap-3 tablet:gap-4">
					<LayoutGroup>
						{nodes?.map((child, index) => (
							<motion.div
								key={index}
								className={clsx(
									"min-w-0 shrink-0 grow-0 basis-[90%] desktop:basis-[calc(25%-16px)] laptop:basis-[calc(33.333%-16px)] phablet:basis-[calc(33.333%-16px)] tablet:basis-[calc(25%-16px)]",
									className,
								)}
							>
								{child}
							</motion.div>
						))}
					</LayoutGroup>

					{href && nodes.length >= 6 && (
						<div className="flex w-64 shrink-0 cursor-pointer items-center justify-center rounded-xl bg-slate-50/50 hover:bg-slate-50 dark:bg-slate-700/50 dark:hover:bg-slate-700">
							<Link
								href={href}
								className="flex items-center gap-1.5 font-semibold text-2xl"
							>
								View More <ChevronRight size={24} />
							</Link>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
